import { CheckedAddress, NormalizedNumber, Percentage, raise } from '@sparkdotfi/common-universal'
import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { times } from 'remeda'
import z from 'zod'

import { SPK_FARM_HISTORY_TIMEFRAMES, SpkFarmChartDetails, SpkFarmHistoryTimeframe } from '../types'
import { getChainConfigEntry } from '@/config/chain'
import { Timeframe } from '@/ui/charts/defaults'
import { useFilterChartDataByTimeframe } from '@/ui/charts/logic/useFilterDataByTimeframe'

export interface UseSpkFarmChartDetailsParams {
  chainId: number
}
export function useSpkFarmChartsDetails({ chainId }: UseSpkFarmChartDetailsParams): SpkFarmChartDetails {
  const [timeframe, setTimeframe] = useState<SpkFarmHistoryTimeframe>('All')
  const filterDataByTimeframe = useFilterChartDataByTimeframe(timeframe)

  const spkFarmConfig =
    getChainConfigEntry(chainId).spk?.farmConfig ?? raise('SPK token config is not defined on this chain')
  const getFarmDetailsApiUrl = spkFarmConfig.getFarmDetailsApiUrl

  const history = useQuery({
    ...farmHistoricDataQueryOptions({
      chainId,
      farmAddress: spkFarmConfig.address,
      historyCutoff: spkFarmConfig.historyCutoff,
      getFarmDetailsApiUrl,
    }),
    select: filterDataByTimeframe,
    enabled: !!getFarmDetailsApiUrl,
  })

  return {
    history,
    onTimeframeChange: (timeframe: Timeframe) => {
      if (SPK_FARM_HISTORY_TIMEFRAMES.includes(timeframe)) {
        setTimeframe(timeframe as any)
      }
    },
    timeframe,
    availableTimeframes: SPK_FARM_HISTORY_TIMEFRAMES,
  }
}

export interface FarmHistoricDataParameters {
  chainId: number
  farmAddress: CheckedAddress
  historyCutoff?: Date
  getFarmDetailsApiUrl: ((address: CheckedAddress, page?: number) => string) | undefined
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function farmHistoricDataQueryOptions({
  chainId,
  farmAddress,
  historyCutoff,
  getFarmDetailsApiUrl,
}: FarmHistoricDataParameters) {
  return queryOptions({
    queryKey: ['farm-historic-data', chainId, farmAddress],
    queryFn: getFarmDetailsApiUrl
      ? async () => {
          const firstRequestUrl: string = getFarmDetailsApiUrl(farmAddress, undefined)

          const res = await fetch(firstRequestUrl)
          if (!res.ok) {
            throw new Error(`Failed to fetch farm data: ${res.statusText}`)
          }

          const parsedFirstResponse = historicDataResponseSchema.parse(await res.json())
          const firstPageResults = parseHistoricDataResponse(parsedFirstResponse.results)

          if (firstPageResults.length === 0) {
            throw new Error('No historic data returned')
          }

          const pagesCount = Math.ceil(parsedFirstResponse.count / firstPageResults.length)

          const followingPagesResults = await Promise.all(
            times(pagesCount - 1, async (index) => {
              const res = await fetch(getFarmDetailsApiUrl(farmAddress, index + 2))
              if (!res.ok) {
                throw new Error(`Failed to fetch farm data: ${res.statusText}`)
              }

              const parsedResponse = historicDataResponseSchema.parse(await res.json())
              return parseHistoricDataResponse(parsedResponse.results)
            }),
          )

          const items = firstPageResults.concat(followingPagesResults.flat())

          if (historyCutoff) {
            return items.filter((result) => result.date >= historyCutoff)
          }

          return items
        }
      : skipToken,
  })
}

const historicDataResponseSchema = z.object({
  count: z.number(),
  results: z.array(
    z.object({
      date: z.string().transform((value) => new Date(value)),
      apr: z.string().transform((value) => Percentage(value, { allowMoreThan1: true })),
      total_staked: z.string().transform((value) => NormalizedNumber(value)),
    }),
  ),
})

function parseHistoricDataResponse(results: z.infer<typeof historicDataResponseSchema>['results']): HistoryItem[] {
  return results.map((result) => ({
    date: result.date,
    apy: result.apr,
    tvl: result.total_staked,
  }))
}

interface HistoryItem {
  date: Date
  apy: Percentage
  tvl: NormalizedNumber
}
